import React, { useState } from 'react';
import { serverApi } from '../api';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

export const ImageDropzone = ({
    label = 'Imagen',
    value = '',
    onChange,
    recommendedSpecs = 'Logo horizontal: 300x80 px | PNG, JPG o SVG (máx. 2MB)',
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);

    const safeValue = typeof value === 'string' ? value : '';

    // Comprime la imagen en el navegador usando Canvas (no aplica a SVG)
    const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.85) => {
        return new Promise((resolve, reject) => {
            // Los SVG no se comprimen, se envían tal cual
            if (file.type === 'image/svg+xml') {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                return;
            }

            const img = new Image();
            const reader = new FileReader();

            reader.readAsDataURL(file);
            reader.onload = (e) => {
                img.src = e.target.result;
            };
            reader.onerror = reject;

            img.onload = () => {
                let { width, height } = img;

                // Redimensionar manteniendo proporción
                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = Math.round(width * ratio);
                    height = Math.round(height * ratio);
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);

                // Intentar WebP primero (mejor compresión), fallback a JPEG
                let compressedBase64 = canvas.toDataURL('image/webp', quality);
                if (!compressedBase64.startsWith('data:image/webp')) {
                    compressedBase64 = canvas.toDataURL('image/jpeg', quality);
                }

                const originalSizeKB = Math.round(file.size / 1024);
                const compressedSizeKB = Math.round((compressedBase64.length * 3) / 4 / 1024);
                console.log(
                    `📦 Imagen comprimida: ${originalSizeKB} KB → ${compressedSizeKB} KB ` +
                    `(${Math.round((1 - compressedSizeKB / originalSizeKB) * 100)}% reducción) ` +
                    `| ${img.naturalWidth}x${img.naturalHeight} → ${width}x${height}`
                );

                resolve(compressedBase64);
            };
            img.onerror = reject;
        });
    };

    const processFile = async (file) => {
        if (!file) return;

        // Validaciones de tipo
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            return MySwal.fire('Formato no válido', 'Por favor seleccione una imagen PNG, JPG, WEBP o SVG.', 'warning');
        }

        // Límite generoso antes de comprimir (10 MB originales)
        if (file.size > 10 * 1024 * 1024) {
            return MySwal.fire('Archivo muy grande', 'El tamaño máximo permitido es de 10 MB.', 'warning');
        }

        try {
            setUploading(true);

            // Comprimir en el navegador antes de enviar
            const base64Data = await compressImage(file);

            const resp = await serverApi.post('/superadmin/upload-imagen', { base64Data });

            if (resp.data.ok) {
                onChange(resp.data.url);
            } else {
                MySwal.fire('Error', resp.data.msg || 'No se pudo subir la imagen', 'error');
            }
            setUploading(false);
        } catch (error) {
            setUploading(false);
            MySwal.fire('Error', 'No se pudo procesar o subir la imagen', 'error');
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    return (
        <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>
                {label}
            </label>

            {/* Zona Drag and Drop / Preview */}
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                style={{
                    border: `2px dashed ${isDragging ? 'var(--color-primario)' : '#cbd5e1'}`,
                    borderRadius: 14,
                    background: isDragging ? '#f0fdf4' : '#f8fafc',
                    padding: 16,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    cursor: 'pointer',
                }}
            >
                {!safeValue && !uploading && (
                    <input
                        type="file"
                        accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                        onChange={handleFileSelect}
                        style={{
                            position: 'absolute',
                            top: 0, left: 0, width: '100%', height: '100%',
                            opacity: 0, cursor: 'pointer', zIndex: 1
                        }}
                    />
                )}

                {uploading ? (
                    <div style={{ padding: 12, color: 'var(--color-primario-texto)', fontWeight: 700 }}>
                        <span className="spinner-border spinner-border-sm me-2"></span> Subiendo imagen...
                    </div>
                ) : safeValue ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img
                                src={safeValue.startsWith('http') ? safeValue : `${serverApi.defaults.baseURL.replace('/api', '')}${safeValue}`}
                                alt="preview"
                                onError={(e) => {
                                    e.target.src = safeValue;
                                }}
                                style={{ maxHeight: 52, maxWidth: 120, objectFit: 'contain', borderRadius: 8, background: '#ffffff', padding: 4, border: '1px solid #e2e8f0' }}
                            />
                            <div style={{ textAlign: 'left' }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', display: 'block' }}>✓ Imagen cargada</span>
                                <span style={{ fontSize: 11, color: '#64748b', wordBreak: 'break-all' }}>
                                    {safeValue.length > 35 ? safeValue.substring(0, 35) + '...' : safeValue}
                                </span>
                            </div>
                        </div>

                        <div style={{ zIndex: 2, display: 'flex', gap: 6 }}>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange('');
                                }}
                                style={{
                                    background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c',
                                    borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                                }}
                            >
                                Cambiar / Quitar
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 24, marginBottom: 4, color: 'var(--color-primario-texto)' }}>📁</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                            Arrastra y suelta tu archivo aquí o <span style={{ color: 'var(--color-primario-texto)', textDecoration: 'underline' }}>examina</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                            {recommendedSpecs}
                        </div>
                    </div>
                )}
            </div>

            {/* Toggle para ingresar URL manual */}
            <div style={{ marginTop: 6, textAlign: 'right' }}>
                <button
                    type="button"
                    onClick={() => setShowUrlInput(!showUrlInput)}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}
                >
                    {showUrlInput ? 'Ocultar campo URL' : '¿Prefieres pegar una URL?'}
                </button>
            </div>

            {showUrlInput && (
                <input
                    type="text"
                    value={safeValue}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="https://servidor.com/logo.png"
                    style={{
                        width: '100%', padding: '8px 12px', borderRadius: 8,
                        border: '1.5px solid #cbd5e1', marginTop: 4, fontSize: 13, boxSizing: 'border-box'
                    }}
                />
            )}
        </div>
    );
};
